import { useState } from 'react'
import {
  Stack, Text, Table, Button, Badge, Modal,
  TextInput, Textarea, Select, Group, Switch, Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconEdit, IconTrash, IconAlertCircle, IconForms } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { DataCard, EmptyState } from '@/components/ui'

interface Category {
  id: number
  name: string
}

interface FormTemplate {
  id: number
  tenant_id: number
  name: string
  description: string
  category_id: number | null
  category_name?: string
  default_priority: string
  default_request_type: string
  title_prefix: string
  description_template: string
  is_active: boolean
}

interface FormTemplatePayload {
  name: string
  description: string
  category_id?: number
  default_priority: string
  default_request_type: string
  title_prefix: string
  description_template: string
  is_active: boolean
}

function TemplateModal({
  opened, onClose, initial, tenantId,
}: {
  opened: boolean
  onClose: () => void
  initial: FormTemplate | null
  tenantId: number | undefined
}) {
  const queryClient = useQueryClient()
  const isEdit = !!initial

  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [categoryId, setCategoryId] = useState<string | null>(
    initial?.category_id ? String(initial.category_id) : null
  )
  const [priority, setPriority] = useState(initial?.default_priority ?? 'medium')
  const [rtype, setRtype] = useState(initial?.default_request_type ?? 'service_request')
  const [titlePrefix, setTitlePrefix] = useState(initial?.title_prefix ?? '')
  const [descriptionTemplate, setDescriptionTemplate] = useState(initial?.description_template ?? '')
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['ticket-categories', tenantId],
    queryFn: async () => {
      const res = await api.get<Category[]>(`/v1/tenants/${tenantId}/ticket-categories`)
      return res.data
    },
    enabled: !!tenantId && opened,
  })

  const categoryOptions = categories.map((c) => ({
    value: String(c.id),
    label: c.name,
  }))

  const mutation = useMutation({
    mutationFn: async (payload: FormTemplatePayload) => {
      if (isEdit && initial) {
        await api.put(`/v1/tenants/${tenantId}/form-templates/${initial.id}`, payload)
      } else {
        await api.post(`/v1/tenants/${tenantId}/form-templates`, payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-templates', tenantId] })
      notifications.show({ color: 'green', message: `Template ${isEdit ? 'atualizado' : 'criado'} com sucesso!` })
      onClose()
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro: ${err.message}` })
    },
  })

  const handleSubmit = () => {
    if (!name.trim()) {
      notifications.show({ color: 'orange', message: 'Nome é obrigatório.' })
      return
    }
    mutation.mutate({
      name: name.trim(),
      description: description.trim(),
      category_id: categoryId ? Number(categoryId) : undefined,
      default_priority: priority,
      default_request_type: rtype,
      title_prefix: titlePrefix.trim(),
      description_template: descriptionTemplate.trim(),
      is_active: isActive,
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{isEdit ? 'Editar Template' : 'Novo Template'}</Text>}
      size="lg"
    >
      <Stack gap="sm">
        <TextInput label="Nome" required value={name} onChange={(e) => setName(e.target.value)} />
        <Textarea label="Descrição" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        <Group grow>
          <Select
            label="Categoria"
            placeholder={categories.length === 0 ? 'Nenhuma categoria cadastrada' : 'Selecionar categoria'}
            clearable
            searchable
            nothingFoundMessage="Nenhuma categoria cadastrada"
            data={categoryOptions}
            value={categoryId}
            onChange={setCategoryId}
          />
          <Select
            label="Prioridade padrão"
            value={priority}
            onChange={(v) => setPriority(v ?? 'medium')}
            data={[
              { value: 'low', label: 'Baixa' },
              { value: 'medium', label: 'Média' },
              { value: 'high', label: 'Alta' },
              { value: 'critical', label: 'Crítica' },
            ]}
          />
        </Group>
        <Group grow>
          <Select
            label="Tipo padrão"
            value={rtype}
            onChange={(v) => setRtype(v ?? 'service_request')}
            data={[
              { value: 'approval', label: 'Aprovação' },
              { value: 'incident', label: 'Incidente' },
              { value: 'service_request', label: 'Serviço' },
              { value: 'change_request', label: 'Mudança' },
              { value: 'gmud', label: 'GMUD' },
            ]}
          />
          <TextInput
            label="Prefixo do título"
            placeholder="Ex: [INFRA]"
            value={titlePrefix}
            onChange={(e) => setTitlePrefix(e.target.value)}
          />
        </Group>
        <Textarea
          label="Template de descrição"
          placeholder="Texto pré-preenchido no campo descrição ao usar este template"
          rows={4}
          value={descriptionTemplate}
          onChange={(e) => setDescriptionTemplate(e.target.value)}
        />
        {isEdit && (
          <Switch
            label="Template ativo"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={mutation.isPending}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function FormTemplates() {
  const tenantId = useCurrentTenant()?.id
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editTemplate, setEditTemplate] = useState<FormTemplate | null>(null)

  const { data: templates = [], isLoading, error } = useQuery<FormTemplate[]>({
    queryKey: ['form-templates', tenantId],
    queryFn: async () => {
      const res = await api.get<FormTemplate[]>(`/v1/tenants/${tenantId}/form-templates`)
      return res.data
    },
    enabled: !!tenantId,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/v1/tenants/${tenantId}/form-templates/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-templates', tenantId] })
      notifications.show({ color: 'green', message: 'Template removido com sucesso!' })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro: ${err.message}` })
    },
  })

  const handleNew = () => { setEditTemplate(null); setModalOpen(true) }
  const handleEdit = (tpl: FormTemplate) => { setEditTemplate(tpl); setModalOpen(true) }
  const handleDelete = (tpl: FormTemplate) => {
    if (window.confirm(`Remover template "${tpl.name}"?`)) {
      deleteMutation.mutate(tpl.id)
    }
  }

  const priorityColor = (p: string) =>
    p === 'critical' ? 'red' : p === 'high' ? 'orange' : p === 'medium' ? 'blue' : 'gray'

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar os templates. Tente novamente em instantes.
        </Alert>
      )}

      <DataCard flush
        title="Templates de Formulário"
        accent="primary"
        badge={{ value: templates.length, color: 'blue' }}
        loading={isLoading}
        extra={
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={handleNew} disabled={!tenantId}>
            Novo Template
          </Button>
        }
      >
        {templates.length === 0 ? (
          <EmptyState
            bare
            size="md"
            accent="info"
            icon={IconForms}
            title="Nenhum template cadastrado"
            description="Templates definem campos customizados por categoria de chamado (ex: solicitação de acesso pede usuário e sistema). Crie o primeiro para começar."
            primaryAction={{
              label: 'Novo Template',
              icon: <IconPlus size={14} />,
              onClick: handleNew,
            }}
          />
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Categoria</Table.Th>
                <Table.Th>Prioridade</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>Prefixo</Table.Th>
                <Table.Th>Ativo</Table.Th>
                <Table.Th>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {templates.map((tpl) => (
                <Table.Tr key={tpl.id}>
                  <Table.Td fw={500}>{tpl.name}</Table.Td>
                  <Table.Td><Text size="xs">{tpl.category_name ?? '—'}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={priorityColor(tpl.default_priority)} variant="light">
                      {tpl.default_priority}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Badge size="xs" variant="outline">{tpl.default_request_type}</Badge></Table.Td>
                  <Table.Td><Text size="xs">{tpl.title_prefix || '—'}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={tpl.is_active ? 'green' : 'gray'} variant="light">
                      {tpl.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconEdit size={12} />}
                        onClick={() => handleEdit(tpl)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={12} />}
                        onClick={() => handleDelete(tpl)}
                        loading={deleteMutation.isPending}
                      >
                        Excluir
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>

      <TemplateModal
        key={editTemplate?.id ?? 'new'}
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        initial={editTemplate}
        tenantId={tenantId ? Number(tenantId) : undefined}
      />
    </Stack>
  )
}
